import {
  transformedGatewaysWithMachinesListEntryTypes,
  MachinesListDataTypes,
  MachinesListSupportSessionsStatusTypes,
} from "@common/types/machines-list-types";

type SupportSessionUpdate = {
  source_id?: string;
  status?: string | MachinesListSupportSessionsStatusTypes;
  remaining_duration?: string | null;
};

type SupportSessionsPayload =
  | SupportSessionUpdate
  | Record<string, MachinesListDataTypes[]>;

type MergeGatewaySourcesDataPropsTypes = {
  sources: Record<string, MachinesListDataTypes>;
  supportSessionsBySourceIdMap: Map<string, SupportSessionUpdate>;
  isPollingResponse: boolean;
};

type machinesMergeSupportSessionsListDataPropsTypes = {
  allMachinesListData: Record<
    string,
    transformedGatewaysWithMachinesListEntryTypes
  >;
  supportSessionsPayload: SupportSessionsPayload;
  isPollingResponse?: boolean;
  isPostSupportSessionResponse?: boolean;
};

type mergeGatewaySourcesWithSupportSessionsDataReturnType = {
  updatedSources: Record<string, MachinesListDataTypes>;
  didSourceChanged: boolean;
};

// based on POST or GET , seperate out this function.
const buildSupportSessionsMapForPost = (
  supportSessionsPayload: SupportSessionUpdate,
  map: Map<string, SupportSessionUpdate>,
): Map<string, SupportSessionUpdate> => {
  if (!supportSessionsPayload?.source_id) {
    return map;
  }
  // nosemgrep: nodejs_scan.javascript-headers-rule-generic_header_injection
  map.set(supportSessionsPayload.source_id, supportSessionsPayload);
  return map;
};

const buildSupportSessionsMapForPolling = (
  supportSessionsPayload: Record<string, MachinesListDataTypes[]>,
  map: Map<string, SupportSessionUpdate>,
): Map<string, SupportSessionUpdate> => {
  Object.entries(supportSessionsPayload).forEach(
    ([sourceIdKey, sourceEntryData]) => {
      if (sourceEntryData.length === 0) return;
      map.set(sourceIdKey, sourceEntryData[0]);
    },
  );

  return map;
};

/**
 * Normalizes status to uppercase enum value, defaults to INITIAL if undefined or invalid
 * @param status - Status string to normalize
 * @returns Normalized status as MachinesListSupportSessionsStatusTypess
 */

const normalizeStatus = (
  status?: string | MachinesListSupportSessionsStatusTypes,
): MachinesListSupportSessionsStatusTypes =>
  (status?.toUpperCase() ||
    MachinesListSupportSessionsStatusTypes.INITIAL) as MachinesListSupportSessionsStatusTypes;

/**
 * Merges server entry with matched response data
 * and normalizes status to uppercase enum value
 *
 * @param serverEntry - Existing server list entry
 * @param matchedData - Matched response data for the source
 * @returns Merged server entry
 */
const mergeEntry = (
  serverEntry: MachinesListDataTypes,
  matchedData: SupportSessionUpdate,
): MachinesListDataTypes => {
  const status = matchedData?.status;
  return {
    ...serverEntry,
    ...matchedData,
    status: normalizeStatus(status),
  };
};

/**
 * Resets status back to INITIAL when polling data no longer contains the source
 *
 * @param entry - Server list entry
 * @returns Entry with reset status
 */
const removeStatusAndSetInitial = (
  entry: MachinesListDataTypes,
): MachinesListDataTypes => ({
  ...entry,
  status: MachinesListSupportSessionsStatusTypes.INITIAL,
});

/**
 * Detects if a value has changed between two objects
 *
 * @param oldVal - Previous value
 * @param newVal - New value
 * @returns True if values differ
 */
const didSourceChanged = (oldVal?: string, newVal?: string): boolean =>
  oldVal !== newVal;

/**
 * Processes nested sources within gateway objects
 * Merges matched response data and updates Redux status
 *
 * @param sources - Source map within a gateway
 * @param supportSessionsPayload - API response containing updates
 * @param dispatch - Redux dispatch function
 * @param isPollingResponse - Indicates whether response is from polling
 * @returns Updated sources and whether changes occurred
 */
const mergeGatewaySourcesWithSupportSessionsData = ({
  sources,
  supportSessionsBySourceIdMap,
  isPollingResponse,
}: MergeGatewaySourcesDataPropsTypes): mergeGatewaySourcesWithSupportSessionsDataReturnType => {
  let sourceChanged = false;

  const updatedSources: Record<string, MachinesListDataTypes> = {};
  // Iterates through each source in the gateway and merges with matched response data based on source_id
  Object.keys(sources).forEach((sourceIdKey) => {
    const sourceEntry = sources[sourceIdKey];
    const matchedData =
      supportSessionsBySourceIdMap.get(sourceEntry.source_id) ?? null;

    if (matchedData) {
      // If there's matched data for the source, merge it and check for status changes
      if (
        didSourceChanged(
          sourceEntry.status,
          normalizeStatus(matchedData?.status),
        ) ||
        didSourceChanged(
          sourceEntry.remaining_duration,
          matchedData.remaining_duration,
        )
      ) {
        const updatedEntry = mergeEntry(sourceEntry, matchedData);
        sourceChanged = true;
        updatedSources[sourceIdKey] = updatedEntry;
        return;
      }

      updatedSources[sourceIdKey] = sourceEntry;
      return;
      // did not match any data for this source in the response, if this is a polling response and the source has an existing status, reset it to INITIAL
    } else if (isPollingResponse && sourceEntry.status) {
      if (
        sourceEntry.status !== MachinesListSupportSessionsStatusTypes.INITIAL
      ) {
        const resetEntry = removeStatusAndSetInitial(sourceEntry);
        sourceChanged = true;
        updatedSources[sourceIdKey] = resetEntry;
        return;
      }
    }

    updatedSources[sourceIdKey] = sourceEntry;
  });

  return { updatedSources, didSourceChanged: sourceChanged };
};

/**
 * Merges support session updates into the existing gateway and sources structure
 *
 * - Handles gateway objects containing nested sources
 * - Updates Redux only when status changes
 * - Clears stale statuses during polling
 * - Returns null when no changes are detected to avoid unnecessary updates
 *
 * @param allMachinesListData - Current gateway/server list data with nested sources
 * @param supportSessionsPayload - API response containing updates
 * @param dispatch - Redux dispatch function
 * @param isPollingResponse - Indicates whether response is from polling
 * @returns Updated gateway list with merged sources or null if no changes
 */
export const machinesMergeSupportSessionsListData = ({
  allMachinesListData,
  supportSessionsPayload,
  isPollingResponse = false,
  isPostSupportSessionResponse = false,
}: machinesMergeSupportSessionsListDataPropsTypes): Record<
  string,
  transformedGatewaysWithMachinesListEntryTypes
> | null => {
  // Flag to determine if we should pass merged data back to Redux or if there are no changes
  let shouldPassMergedData = false;

  const mergedData: Record<
    string,
    transformedGatewaysWithMachinesListEntryTypes
  > = {};

  // creates a Map of source_id to support session updates for quick lookup during merging
  const map = new Map<string, SupportSessionUpdate>();
  let supportSessionsBySourceIdMap: Map<string, SupportSessionUpdate>;

  if (isPostSupportSessionResponse) {
    supportSessionsBySourceIdMap = buildSupportSessionsMapForPost(
      supportSessionsPayload,
      map,
    );
  } else {
    supportSessionsBySourceIdMap = buildSupportSessionsMapForPolling(
      supportSessionsPayload as Record<string, MachinesListDataTypes[]>,
      map,
    );
  }

  // Iterates through each gateway entry in the server list and processes its nested sources to merge with matched response data
  Object.values(allMachinesListData).forEach((gatewayOrEntry) => {
    const {
      updatedSources,
      didSourceChanged,
    }: mergeGatewaySourcesWithSupportSessionsDataReturnType =
      mergeGatewaySourcesWithSupportSessionsData({
        sources: gatewayOrEntry.sources,
        supportSessionsBySourceIdMap,
        isPollingResponse,
      });

    if (didSourceChanged) {
      shouldPassMergedData = true;

      // If any source within the gateway has changed, we create a new gateway object with the updated sources to maintain immutability
      mergedData[gatewayOrEntry?.name] = {
        ...gatewayOrEntry,
        sources: updatedSources,
      };
    }
  });

  return shouldPassMergedData ? mergedData : null;
};
